import { createProduct, updateProduct } from "../api/productApi";
import {
  BeerFormState,
  ProductImage,
  ProductData,
  SanityData,
  BeerFormStateEdit,
  BackendImage,
  ProductToGeneratePresignedUrl,
} from "../constants/types";
import { productEndPoints } from "../constants/endpoints";
import { getPresignedUrls, uploadFileToS3Api } from "../api/customerAPI";
import bucketMapping from "../constants/S3BuketMapping";
import { store } from "../redux/store";
import { setLoading } from "../redux/features/slices/appSlice";

export const createProductService = async (data: BeerFormState) => {
  try {
    const productData = extractProductData(data);
    const sanityData = getSanityData(data);

    const updatedProductImagesToUpload = await convertFilesToProductImages(
      data.fileUpload
    );
    const imagesForPresignedUrl = await convertFilesToGeneratePresignedUrl(
      data.fileUpload
    );

    const presignedUrls = await getPresignedUrls(
      imagesForPresignedUrl,
      bucketMapping.product
    );

    const finalUploadedDoc: any[] = await uploadMultipleFilesWithPresignedUrls(
      presignedUrls as {
        presignedUrl: string;
        key: string;
        fileName: string;
      }[],
      updatedProductImagesToUpload
    );

    const productImages = finalUploadedDoc.map((uploadedFile) => ({
      url: uploadedFile.documentId?.split("?")[0],
      fileName: uploadedFile.fileName,
    }));

    const response = await createProduct(
      {
        productData,
        productImages,
        sanityData,
      },
      productEndPoints.createNewProduct
    );
    return response;
  } catch (error: any) {
    console.log("error", error);
    throw new Error(error.message);
  }
};

export const extractProductData = (input: BeerFormState): ProductData => {
  const priceInCents = parseFloat(input.price) * 100;
  const discountedPriceInCents = 0;
  const disabled = false;
  return {
    priceInCents,
    discountedPriceInCents,
    disabled,
    ibu: Number(input.ibu),
    abv: Number(input.abv),
  };
};

export const convertFilesToProductImages = async (
  files: File[]
): Promise<ProductImage[]> => {
  const productImages: ProductImage[] = await Promise.all(
    files.map(async (file) => {
      return {
        fileName: file.name,
        fileContent: file,
        fileType: file.type,
      };
    })
  );
  return productImages;
};

export const convertFilesToGeneratePresignedUrl = async (
  files: File[]
): Promise<ProductToGeneratePresignedUrl[]> => {
  const productImages: ProductToGeneratePresignedUrl[] = await Promise.all(
    files.map(async (file) => {
      return {
        fileName: file.name,
        fileType: file.type,
      };
    })
  );
  return productImages;
};

export const getSanityData = (data: {
  beerName: string;
  description: string;
}): SanityData => {
  return {
    name: data.beerName,
    description: data.description,
    primaryButtonTitle: "Buy Now",
    secondaryButtonTitle: "Learn More",
  };
};

export const mapBackendToFormState = (
  backendResponse: any
): BeerFormStateEdit => {
  return {
    beerName: backendResponse.name,
    quantity: "0", // Populate as needed
    description: backendResponse.description,
    price: (backendResponse.priceInCents / 100).toString(),
    fileUpload: [], // No files uploaded initially
    existingImages: backendResponse.images.map((image: BackendImage) => ({
      url: image.url,
      fileName: image.fileName,
    })),
    ibu: backendResponse.ibu,
    abv: backendResponse.abv,
  };
};

export const updateProductService = async (
  data: BeerFormStateEdit,
  id: string
) => {
  try {
    const productData = extractProductData(data);

    const productImages = data.existingImages;
    const sanityData = getSanityData(data);

    const updatedProductImagesToUpload = await convertFilesToProductImages(
      data.fileUpload
    );

    const imagesForPresignedUrl = await convertFilesToGeneratePresignedUrl(
      data.fileUpload
    );

    const presignedUrls = await getPresignedUrls(
      imagesForPresignedUrl,
      bucketMapping.product
    );

    const finalUploadedDoc: any[] = await uploadMultipleFilesWithPresignedUrls(
      presignedUrls as {
        presignedUrl: string;
        key: string;
        fileName: string;
      }[],
      updatedProductImagesToUpload
    );

    const updatedProductImages = finalUploadedDoc?.map((uploadedFile) => ({
      url: uploadedFile.documentId?.split("?")[0],
      fileName: uploadedFile.fileName,
    }));

    const response = await updateProduct(
      {
        productData,
        updatedProductImages,
        productImages,
        sanityData,
      },
      productEndPoints.createNewProduct,
      id
    );

    return response;
  } catch (error: any) {
    console.log("error", error);
    throw Error(error);
  }
};

export const uploadMultipleFilesWithPresignedUrls = async (
  presignedUrls: { presignedUrl: string; key: string; fileName: string }[],
  documents: ({
    fileContent: string | File | Blob;
    fileType: string;
    fileName: string;
  } | null)[]
) => {
  // Filter out null documents before processing
  const validDocuments = documents.filter(
    (
      doc
    ): doc is {
      fileContent: File | Blob;
      fileType: string;
      fileName: string;
    } => doc !== null && doc.fileName.trim() !== ""
  );

  store.dispatch(setLoading(true));
  const uploadedDocuments = await Promise.all(
    presignedUrls.map(async (presignedFile, index) => {
      const document = validDocuments[index]; // Ensure correct mapping

      if (!document) {
        console.warn(
          `Skipping upload: No document found for ${presignedFile.fileName}`
        );
        return null;
      }

      const isUploaded = await uploadSingleFileWithPresignedUrl(
        presignedFile.presignedUrl,
        document.fileContent,
        document.fileType
      );

      if (isUploaded) {
        return {
          documentId: presignedFile.presignedUrl, // S3 URL
          fileName: document.fileName,
          key: presignedFile.key, // S3 key
        };
      } else {
        throw new Error(`Upload failed for ${document.fileName}`);
      }
    })
  );
  store.dispatch(setLoading(false));

  // Remove null values before returning
  return uploadedDocuments.filter((doc) => doc !== null);
};

export const uploadSingleFileWithPresignedUrl = async (
  presignedUrl: string,
  binaryData: File | Blob,
  fileType: string
) => {
  try {
    const blobData =
      binaryData instanceof Blob
        ? binaryData
        : new Blob([binaryData], { type: fileType });
    const response: any = await uploadFileToS3Api(
      presignedUrl,
      blobData,
      fileType
    );

    return response;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw new Error("File upload failed");
  }
};
